import React, {useEffect, useState} from 'react';
import {UserComment} from 'records/UserComment';
import {CallTick} from '@components/call-card/CallTick';
import './CallComment.sass';
import {CallRegion} from '@components/call-card/CallRegion';
import {PlayerProps} from '@components/call-card/Player.utils';
import {FileUploadId} from 'records/CoachingReviewFileUpload';
import {SetState} from '@store/@types/util/React';
import {resizeHighlight} from '@components/call-card/Comment.utils';
import {Seconds} from '@store/@types/util';
import {CallCommentPopper} from '@components/call-card/CallCommentPopper';

interface CallCommentProps extends PlayerProps {
  comment: UserComment;
  callId: FileUploadId;
  canDelete: boolean;
  setEditComment?: SetState<UserComment | undefined>;
  bucketIndex?: number;
  // Are we in mini comment mode?
  mini?: boolean;
  // Show comment popper without mouseover, can be dismissed
  defaultOpen?: boolean;
  // Hide comment popper no matter what
  hidePopper?: boolean;
  setTimeSeconds: (seconds: Seconds) => void;
  playRecording: () => void;
}

export const CallComment: React.FC<CallCommentProps> = ({
  comment,
  callId,
  bucketIndex = 0,
  mini,
  defaultOpen,
  hidePopper,
  canDelete,
  setEditComment,
  setTimeSeconds,
  playRecording,
  ...playerProps
}) => {
  const {duration} = playerProps;
  const [commentRef, setCommentRef] = useState<HTMLDivElement | null>(null);
  const [open, setOpen] = useState<any>(null);

  useEffect(() => {
    if (defaultOpen && commentRef) {
      setOpen(commentRef);
    }
  }, [commentRef, defaultOpen]);

  const handleClick = () => {
    setTimeSeconds(comment.startTime);
    playRecording();
  };

  const commentMarker = comment.isRange() ? (
    <CallRegion
      setRef={setCommentRef}
      duration={duration}
      onMouseEnter={e => setOpen(e.currentTarget)}
      onMouseLeave={() => setOpen(null)}
      startTime={comment.startTime}
      endTime={comment.endTime}
      className={open ? 'open' : undefined}
      type="comment"
      bucketIndex={bucketIndex}
      mini={!!mini}
      onResize={
        !!setEditComment
          ? (startTime, endTime) =>
              resizeHighlight(
                callId,
                comment.set('startTime', startTime).set('endTime', endTime),
                duration
              )
          : undefined
      }
      trackWidth={commentRef?.parentElement?.getBoundingClientRect().width}
      color={comment.color}
      onClick={handleClick}
    />
  ) : (
    <CallTick
      setRef={setCommentRef}
      onMouseEnter={e => setOpen(e.currentTarget)}
      onMouseLeave={() => setOpen(null)}
      duration={duration}
      location={comment.startTime}
      type="comment"
      onClick={handleClick}
    />
  );

  return (
    <div className="call-comment">
      {!hidePopper && (
        <CallCommentPopper
          commentRef={commentRef}
          comment={comment}
          setEditComment={setEditComment}
          canDelete={canDelete}
          callId={callId}
          open={open}
        />
      )}
      {commentMarker}
    </div>
  );
};
